import React, { useState, useEffect, useContext } from "react";
import { Building2, Copy } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import DetailHeader from "../components/DetailHeader";
import FormFooter from "../components/FormFooter";
import Button from "../../../common/Button";
import TextInput from "../../../common/TextInput";
import Validation from "../../../common/Validation";
import ApprovalTeamMembersEditor from "./ApprovalTeamMembersEditor";
import { getActiveCompanyOptions } from "./AccessGroupsList";
import AuditHistory from "../components/AuditHistory";
import DeleteApprovalTeamModal from "./ApprovalTeamsList/DeleteApprovalTeamModal";
import RemoveAllMembersModal from "./ApprovalTeamsList/RemoveAllMembersModal";
import CopyApprovalTeamModal from "./ApprovalTeamsList/CopyApprovalTeamModal";
import "./RoleDetail.css";

export default function ApprovalTeamDetail({
  team,
  onBack,
  onUpdate,
  startEditing = false,
}) {
  const {
    updateApprovalTeam,
    createApprovalTeam,
    deleteApprovalTeam,
    fetchApprovalTeams,
    approvalTeams,
    companies,
  } = useContext(FieldConfigurationContext);
  const [showRemoveAllPrompt, setShowRemoveAllPrompt] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySuccessMessage, setCopySuccessMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(startEditing);
  const [displayTeam, setDisplayTeam] = useState(team);
  const [formData, setFormData] = useState({
    name: team?.name || "",
    company_users: (team?.company_users || []).map((u) =>
      typeof u === "string" ? u : u.uuid
    ),
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);

  useEffect(() => {
    if (!team?.uuid) return;
    FieldConfigurationAPI.getApprovalTeam(team.uuid, team.company_uuid).then(
      (response) => {
        if (response.status === 200 && response.data) {
          setDisplayTeam(response.data);
        }
      }
    );
  }, [team?.uuid, team?.company_uuid]);

  const memberUuids = (displayTeam.company_users || []).map((u) =>
    typeof u === "string" ? u : u.uuid
  );

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required.";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Name must be 100 characters or fewer.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartEdit = () => {
    setFormData({
      name: displayTeam?.name || "",
      company_users: memberUuids,
    });
    setErrors({});
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (!window.confirm("Are you sure you want to cancel?")) return;
    setFormData({
      name: displayTeam?.name || "",
      company_users: memberUuids,
    });
    setErrors({});
    setSaveError(null);
    setIsEditing(false);
  };

  const handleCancelRemoval = () => {
    setShowRemoveAllPrompt(false);
    setFormData((prev) => ({ ...prev, company_users: memberUuids }));
  };

  const handleChooseDelete = () => {
    setShowRemoveAllPrompt(false);
    fetchApprovalTeams();
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (memberUuids.length > 0 && formData.company_users.length === 0) {
      setShowRemoveAllPrompt(true);
      return;
    }
    setIsSaving(true);
    setSaveError(null);

    const payload = {
      name: formData.name.trim(),
      company_users: formData.company_users,
    };

    const result = await updateApprovalTeam(
      displayTeam.uuid,
      payload,
      displayTeam?.company_uuid
    );

    setIsSaving(false);

    if (result?.success) {
      setDisplayTeam(result.data);
      onUpdate && onUpdate(result.data);
      setIsEditing(false);
      setAuditRefreshKey((k) => k + 1);
    } else {
      setSaveError(result?.error || "Failed to save approval team.");
    }
  };

  const isMultiCompany = getActiveCompanyOptions(companies).length > 1;
  const companyName =
    companies?.data?.[displayTeam.company_uuid] || displayTeam.company_uuid;

  const canCopyToCompany =
    getActiveCompanyOptions(companies, companies?.configureApprovalGroupsData)
      .length > 0;

  const handleCopied = (newTeam, targetCompanyName, removedCount) => {
    setCopyModalOpen(false);
    const removedNote =
      removedCount > 0
        ? ` ${removedCount} member${
            removedCount === 1 ? " was" : "s were"
          } left out because they aren't available in that company.`
        : "";
    setCopySuccessMessage(
      `Copied "${newTeam?.name}" to ${targetCompanyName}.${removedNote}`
    );
    fetchApprovalTeams(true);
  };

  return (
    <div className="role-detail">
      {isEditing ? (
        <DetailHeader
          title={displayTeam.name}
          subtitle={
            isMultiCompany && (
              <>
                <Building2 size={14} />
                <span>{companyName}</span>
              </>
            )
          }
          onBack={handleCancelEdit}
          isEditing={false}
        />
      ) : (
        <DetailHeader
          title={displayTeam.name}
          subtitle={
            isMultiCompany && (
              <>
                <Building2 size={14} />
                <span>{companyName}</span>
              </>
            )
          }
          onBack={onBack}
          onEdit={handleStartEdit}
          isEditing={false}
          backLabel="Back to Approval Teams"
          editLabel="Edit Approval Team"
        />
      )}

      <div className="role-detail__content">
        <div className="role-detail__section role-detail__section--info">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h3 className="role-detail__section-title">Team Information</h3>
            {!isEditing && canCopyToCompany && (
              <Button
                className="button--secondary"
                onClick={() => {
                  setCopySuccessMessage(null);
                  setCopyModalOpen(true);
                }}
              >
                <Copy size={16} style={{ marginRight: "6px" }} />
                Copy
              </Button>
            )}
          </div>
          {copySuccessMessage && (
            <p style={{ color: "#16a34a", fontSize: "14px" }}>
              {copySuccessMessage}
            </p>
          )}
          {saveError && <Validation>{saveError}</Validation>}
          <div className="role-detail__fields">
            <div className="role-detail__field">
              <label className="role-detail__label">Team Name</label>
              {isEditing ? (
                <>
                  <TextInput
                    required
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setFormData((prev) => ({ ...prev, name }));
                    }}
                    placeholder="Enter team name"
                    maxLength={100}
                  />
                  {errors.name && <Validation>{errors.name}</Validation>}
                </>
              ) : (
                <span className="role-detail__value">{displayTeam.name}</span>
              )}
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Created</label>
              <span className="role-detail__value">
                {displayTeam.created_at
                  ? new Date(displayTeam.created_at).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Company</label>
              <span className="role-detail__value">{companyName}</span>
            </div>
          </div>
        </div>

        <ApprovalTeamMembersEditor
          companyUuid={displayTeam.company_uuid}
          memberUuids={isEditing ? formData.company_users : memberUuids}
          initialMembers={displayTeam.company_users}
          onChangeMemberUuids={(uuids) =>
            setFormData((prev) => ({ ...prev, company_users: uuids }))
          }
          readOnly={!isEditing}
        />

        <AuditHistory
          resource="approval_teams"
          uuid={displayTeam.uuid}
          refreshKey={auditRefreshKey}
          createdAt={displayTeam.created_at}
        />
      </div>

      {isEditing && (
        <FormFooter
          onCancel={handleCancelEdit}
          onSave={handleSave}
          saveDisabled={isSaving}
          saveLabel={isSaving ? "Saving..." : "Save"}
        />
      )}

      <CopyApprovalTeamModal
        open={copyModalOpen}
        team={displayTeam}
        companies={companies}
        createApprovalTeam={createApprovalTeam}
        onClose={() => setCopyModalOpen(false)}
        onCopied={handleCopied}
      />
      <RemoveAllMembersModal
        open={showRemoveAllPrompt}
        onDelete={handleChooseDelete}
        onCancelRemoval={handleCancelRemoval}
      />
      <DeleteApprovalTeamModal
        open={showDeleteModal}
        team={displayTeam}
        allTeams={approvalTeams.data}
        onClose={() => {
          setShowDeleteModal(false);
          handleCancelRemoval();
        }}
        onDeleted={() => {
          setShowDeleteModal(false);
          onBack();
        }}
        deleteApprovalTeam={deleteApprovalTeam}
      />
    </div>
  );
}
